'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from 'cn';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { Button } from '@efira/ui/components/ui/button';
import { HugeiconsIcon } from '@hugeicons/react';
/*
 * ИКОНКИ - ПОШТУЧНЫМ ПУТЁМ, А НЕ ИЗ ОБЩЕГО ФАЙЛА ПАКЕТА.
 *
 * `@hugeicons/core-free-icons@4.3.4` в своём общем файле импортирует шесть
 * иконок сетки с неверным регистром в имени (`Grid2x2CheckIcon.js` вместо
 * `Grid2X2CheckIcon.js`). На macOS файловая система регистр не различает и
 * сборка проходит; на Linux - CI и Cloudflare Pages - падает шестью
 * «Module not found». Поимённый путь общего файла не касается, и ошибка
 * пакета до нас не доходит; заодно в сборку не тянется перечисление
 * двенадцати тысяч иконок.
 */
import Cancel01Icon from '@hugeicons/core-free-icons/Cancel01Icon';
function Dialog({ ...props }) {
    return _jsx(DialogPrimitive.Root, { "data-slot": "dialog", ...props });
}
function DialogTrigger({ ...props }) {
    return _jsx(DialogPrimitive.Trigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal({ ...props }) {
    return _jsx(DialogPrimitive.Portal, { "data-slot": "dialog-portal", ...props });
}
function DialogClose({ ...props }) {
    return _jsx(DialogPrimitive.Close, { "data-slot": "dialog-close", ...props });
}
function DialogOverlay({ className, ...props }) {
    return (_jsx(DialogPrimitive.Overlay, { "data-slot": "dialog-overlay", "data-dialog-backdrop": "", className: cn(
        /*
         * ПОДЛОЖКА - КАК В ПРЕСЕТЕ (решение владельца 2026-09-26).
         *
         * В единице 13 мы сделали её плотнее поставки: 65 % вместо 10 % и
         * без размытия - за окном настроек оставались насыщенные цвета
         * консоли. Аудит единицы 16 показал обратную сторону: в тёмной
         * теме плотная чёрная плёнка почти ничего не отделяет, потому что
         * фон под ней и так тёмный, а окно светлее его всего на ступень
         * (находка 18).
         *
         * Пресет решает это не плотностью, а РАЗМЫТИЕМ: содержимое под
         * окном перестаёт читаться, оставаясь узнаваемым, и работает это
         * одинаково в обеих темах. Возвращено к поставке.
         */
        'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0', className), ...props }));
}
function DialogContent({ className, children, showCloseButton = true, ...props }) {
    /*
     * НАЧАЛЬНЫЙ ФОКУС - НА САМОМ ОКНЕ, А НЕ НА ПЕРВОМ ОРГАНЕ УПРАВЛЕНИЯ.
     *
     * Находка владельца по парам (единица 15): окно открывается с кольцом
     * фокуса на крестике. Radix при открытии переводит фокус на первый узел
     * внутри, способный его принять, а первым в шапке стоит кнопка закрытия -
     * так во всех двенадцати окнах продукта сразу.
     *
     * Чем это плохо, кроме вида: чтец объявляет не окно, а «Закрыть, кнопка»,
     * то есть человек узнаёт про выход раньше, чем про то, куда он попал. И
     * пробел, нажатый по привычке, окно закрывает.
     *
     * Панель окна у Radix уже `tabindex="-1"` и `outline-none`: фокус на ней
     * даёт чтецу имя окна целиком, кольца не рисует, а `Tab` из неё ведёт на
     * первый орган управления - клавиатурный путь не меняется.
     *
     * Правится ЗДЕСЬ, в примитиве, а не в двух наших обёртках: любое новое
     * окно продукта получает то же поведение, не вспоминая о нём.
     */
    const panel = React.useRef(null);
    return (_jsxs(DialogPortal, { children: [_jsx(DialogOverlay, {}), _jsxs(DialogPrimitive.Content, { "data-slot": "dialog-content", onOpenAutoFocus: (event) => {
                    event.preventDefault();
                    panel.current?.focus();
                }, className: cn(
                /*
                 * ШИРИНА ПО УМОЛЧАНИЮ - БЕЗ `sm:`.
                 *
                 * В поставке здесь стоит `sm:max-w-sm`. Отзывчивый вариант в
                 * каскаде Tailwind идёт ПОЗЖЕ безусловного класса, поэтому
                 * `max-w-5xl`, переданный местом вызова, до окна не доходил, а
                 * `tailwind-merge` их не склеивает: ключи разные. Все окна
                 * продукта - настройки, аналитика, история, смена игры - вместо
                 * своей ширины получали 384 px. Убран префикс: теперь ширина
                 * места вызова вытесняет поставочную, как и задумано shadcn.
                 *
                 * Поля по краям экрана при этом сохранены: они переехали из
                 * `max-w-[calc(100%-2rem)]` в `w-[calc(100%-2rem)]`. Оставить их
                 * в `max-w` было нельзя - `tailwind-merge` склеил бы два `max-w`
                 * в один и узкий экран потерял бы поля.
                 *
                 * ВЫСОТА И ЦЕНТР - С ОГЛЯДКОЙ НА ПОЛОСУ ТОСТОВ. Внизу экрана
                 * зарезервирована полоса `--toast-lane` (`theme.css`): окно
                 * центрируется и упирается в потолок по высоте экрана БЕЗ неё,
                 * поэтому тост никогда не ложится на окно. Прежний потолок
                 * «85 % высоты экрана» заменён этим: 85 % ничем не были
                 * обоснованы, а тут число выведено из высоты тоста.
                 */
                'fixed top-[calc(50%-var(--toast-lane)/2)] left-1/2 z-50 grid max-h-[calc(100dvh-var(--toast-lane)-2rem)] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95', className), ...props, 
                /* Ссылка после раскрытия свойств: начальный фокус обязан найти
                   панель даже там, где место вызова передало свою. */
                ref: panel, children: [children, showCloseButton && (_jsx(DialogPrimitive.Close, { "data-slot": "dialog-close", asChild: true, children: _jsxs(Button, { variant: "ghost", className: "absolute top-2 right-2", size: "icon-sm", children: [_jsx(HugeiconsIcon, { icon: Cancel01Icon, strokeWidth: 2 }), _jsx("span", { className: "sr-only", children: "Close" })] }) }))] })] }));
}
function DialogHeader({ className, ...props }) {
    return (_jsx("div", { "data-slot": "dialog-header", className: cn('flex flex-col gap-2', className), ...props }));
}
function DialogFooter({ className, showCloseButton = false, children, ...props }) {
    return (_jsxs("div", { "data-slot": "dialog-footer", className: cn('-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end', className), ...props, children: [children, showCloseButton && (_jsx(DialogPrimitive.Close, { asChild: true, children: _jsx(Button, { variant: "outline", children: "Close" }) }))] }));
}
function DialogTitle({ className, ...props }) {
    return (_jsx(DialogPrimitive.Title, { "data-slot": "dialog-title", className: cn('font-heading text-base leading-none font-medium', className), ...props }));
}
function DialogDescription({ className, ...props }) {
    return (_jsx(DialogPrimitive.Description, { "data-slot": "dialog-description", className: cn('text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground', className), ...props }));
}
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, };
//# sourceMappingURL=dialog.js.map